import { useEffect, useState } from 'react';
import { AllPartnersResponse, CreatePartnerCategoryPayload, PartnerCategory, Partner } from 'services/partner/partner.types';
import {
    useGetAllPartnersMutation,
    useCreatePartnerCategoryMutation,
    useDeletePartnerCategoryMutation,
    useUpdatePartnerCategoryMutation,
    useDeletePartnerMutation
} from 'services/partner/partner.endpoint';
import { useApiToast } from 'components/Toaster';

export default function usePartnersVM() {
    const [partners, setPartners] = useState<Array<AllPartnersResponse>>();
    const [search, setSearch] = useState<string>('');
    const [filter, setFilter] = useState<{ departments: { label: string; value: string }[]; targets: { label: string; value: string }[] }>({ departments: [], targets: [] });
    const [partnersFiltered, setPartnersFiltered] = useState<Array<AllPartnersResponse>>();
    const [isPartnerCategoryModalOpen, setIsPartnerCategoryModalOpen] = useState<boolean>(false);
    const [partnerCategoryModalMode, setPartnerCategoryModalMode] = useState<'create' | 'edit' | 'delete'>('create');
    const [partnerCategoryToEdit, setPartnerCategoryToEdit] = useState<PartnerCategory>();
    const [partnerCategoryToDelete, setPartnerCategoryToDelete] = useState<PartnerCategory>();
    const [isPartnerModalOpen, setIsPartnerModalOpen] = useState<boolean>(false);
    const [filterIsOpen, setFilterIsOpen] = useState<boolean>(false);
    const [partnerToDelete, setPartnerToDelete] = useState<Partner>();
    const [lastDeletedCategoryLabel, setLastDeletedCategoryLabel] = useState<string>('');
    const [lastDeletedPartnerName, setLastDeletedPartnerName] = useState<string>('');
    const [lastEditedCategoryLabel, setLastEditedCategoryLabel] = useState<string>('');
    const [lastCreatedCategoryLabel, setLastCreatedCategoryLabel] = useState<string>('');

    const { showApiToast, showErrorToast } = useApiToast();

    const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearch(e.target.value);
    };

    const handleNewPartnerCategory = async (data: CreatePartnerCategoryPayload) => {
        setLastCreatedCategoryLabel(data.label);
        await createPartnerCategory(data);
        setIsPartnerCategoryModalOpen(false);
        getAllPartners(undefined);
    };

    const handleEditPartnerCategory = async (data: CreatePartnerCategoryPayload) => {
        if (partnerCategoryToEdit) {
            setLastEditedCategoryLabel(partnerCategoryToEdit.label);
            await updatePartnerCategory({ id: partnerCategoryToEdit.id, label: data.label });
            setIsPartnerCategoryModalOpen(false);
            setPartnerCategoryModalMode('create');
            setPartnerCategoryToEdit(undefined);
            getAllPartners(undefined);
        }
    };

    const handleDeletePartnerCategory = async () => {
        if (partnerCategoryToDelete) {
            setLastDeletedCategoryLabel(partnerCategoryToDelete.label);
            await deletePartnerCategory({ id: partnerCategoryToDelete.id });
            setIsPartnerCategoryModalOpen(false);
            setPartnerCategoryModalMode('create');
            setPartnerCategoryToDelete(undefined);
            getAllPartners(undefined);
        }
    };

    const handleModalTitle = () => {
        switch (partnerCategoryModalMode) {
            case 'create':
                return 'Ajouter une nouvelle catégorie de partenaires';
            case 'edit':
                return 'Modifier la catégorie';
            case 'delete':
                return `Supprimer la catégorie ${partnerCategoryToDelete?.label}`;
            default:
                return '';
        }
    };

    const handleModalYesText = () => {
        switch (partnerCategoryModalMode) {
            case 'create':
                return 'Ajouter';
            case 'edit':
                return 'Modifier';
            case 'delete':
                return 'Supprimer';
            default:
                return '';
        }
    };

    const handleDeletePartner = async () => {
        if (partnerToDelete) {
            setLastDeletedPartnerName(partnerToDelete.name);
            await deletePartner({ id: partnerToDelete.id });
            setIsPartnerModalOpen(false);
            setPartnerToDelete(undefined);
            getAllPartners(undefined);
        }
    };

    const [
        getAllPartners,
        {
            isLoading: isPartnersLoading,
            isError: isPartnersError,
            isSuccess: isPartnersSuccess,
            error: partnersError,
            data: partnersResponse
        }
    ] = useGetAllPartnersMutation();

    const [
        createPartnerCategory,
        {
            isLoading: isCreatePartnerCategoryLoading,
            isError: isCreatePartnerCategoryError,
            isSuccess: isCreatePartnerCategorySuccess,
            error: createPartnerCategoryError,
            data: createPartnerCategoryResponse
        }
    ] = useCreatePartnerCategoryMutation();

    const [
        deletePartnerCategory,
        {
            isLoading: isDeletePartnerCategoryLoading,
            isError: isDeletePartnerCategoryError,
            isSuccess: isDeletePartnerCategorySuccess,
            error: deletePartnerCategoryError,
            data: deletePartnerCategoryResponse
        }
    ] = useDeletePartnerCategoryMutation();

    const [
        updatePartnerCategory,
        {
            isLoading: isUpdatePartnerCategoryLoading,
            isError: isUpdatePartnerCategoryError,
            isSuccess: isUpdatePartnerCategorySuccess,
            error: updatePartnerCategoryError,
            data: updatePartnerCategoryResponse
        }
    ] = useUpdatePartnerCategoryMutation();

    const [
        deletePartner,
        {
            isLoading: isDeletePartnerLoading,
            isSuccess: isDeletePartnerSuccess,
            isError: isDeletePartnerError,
            error: deletePartnerError,
            data: deletePartnerResponse
        }
    ] = useDeletePartnerMutation();

    useEffect(() => {
        setPartnersFiltered(
            partners
                ?.map((category) => ({
                    ...category,
                    partners: category.partners
                        .map((department) => ({
                            ...department,
                            data: department.data.filter((partner) => partner.name.toLowerCase().includes(search.toLowerCase()))
                            .filter((partner) => filter.departments.length > 0 ? filter.departments.map((department) => department.value).includes(partner.department.toLowerCase()) : true)
                            .filter((partner) => filter.targets.length > 0 ? filter.targets.map((target) => target.value).includes(partner.target?.toLowerCase() || "") : true)
                        }))
                        .filter((department) => department.data.length > 0)
                }))
                .filter((category) => ((category.partners.length > 0) && (filter.departments.length > 0 || filter.targets.length > 0)) || (filter.departments.length === 0 && filter.targets.length === 0))
        );
    }, [search, partners, filter]);

    useEffect(() => {
        getAllPartners(undefined);
    }, []);

    useEffect(() => {
        if (isPartnersLoading) return;
        if (isPartnersError && partnersError) {
            showErrorToast(partnersError, {
                message: 'Erreur lors du chargement des partenaires'
            });
            return;
        }
        if (isPartnersSuccess && partnersResponse) {
            setPartners(partnersResponse.data);
            return;
        }
    }, [isPartnersLoading, isPartnersError, isPartnersSuccess, partnersError, partnersResponse, showErrorToast]);

    useEffect(() => {
        if (isDeletePartnerLoading) return;
        if (isDeletePartnerError && deletePartnerError) {
            showErrorToast(deletePartnerError, {
                message: `Erreur lors de la suppression du partenaire "${lastDeletedPartnerName}"`
            });
            return;
        }
        if (isDeletePartnerSuccess && deletePartnerResponse) {
            showApiToast(deletePartnerResponse, {
                successMessage: `Partenaire "${lastDeletedPartnerName}" supprimé avec succès`,
                errorMessage: `Échec de la suppression du partenaire "${lastDeletedPartnerName}"`
            });
            return;
        }
    }, [
        isDeletePartnerLoading,
        isDeletePartnerError,
        isDeletePartnerSuccess,
        deletePartnerError,
        deletePartnerResponse,
        showErrorToast,
        lastDeletedPartnerName
    ]);

    useEffect(() => {
        if (isCreatePartnerCategoryLoading) return;
        if (isCreatePartnerCategoryError && createPartnerCategoryError) {
            showErrorToast(createPartnerCategoryError, {
                message: `Erreur lors de la création de la catégorie "${lastCreatedCategoryLabel}"`
            });
            return;
        }
        if (isCreatePartnerCategorySuccess && createPartnerCategoryResponse) {
            showApiToast(createPartnerCategoryResponse, {
                successMessage: `Catégorie "${lastCreatedCategoryLabel}" créée avec succès`,
                errorMessage: `Échec de la création de la catégorie "${lastCreatedCategoryLabel}"`
            });
            return;
        }
    }, [
        isCreatePartnerCategoryLoading,
        isCreatePartnerCategoryError,
        isCreatePartnerCategorySuccess,
        createPartnerCategoryError,
        createPartnerCategoryResponse,
        showErrorToast,
        lastCreatedCategoryLabel
    ]);

    useEffect(() => {
        if (isUpdatePartnerCategoryLoading) return;
        if (isUpdatePartnerCategoryError && updatePartnerCategoryError) {
            showErrorToast(updatePartnerCategoryError, {
                message: `Erreur lors de la modification de la catégorie "${lastEditedCategoryLabel}"`
            });
            return;
        }
        if (isUpdatePartnerCategorySuccess && updatePartnerCategoryResponse) {
            showApiToast(updatePartnerCategoryResponse, {
                successMessage: `Catégorie "${lastEditedCategoryLabel}" modifiée avec succès`,
                errorMessage: `Échec de la modification de la catégorie "${lastEditedCategoryLabel}"`
            });
            return;
        }
    }, [
        isUpdatePartnerCategoryLoading,
        isUpdatePartnerCategoryError,
        isUpdatePartnerCategorySuccess,
        updatePartnerCategoryError,
        updatePartnerCategoryResponse,
        showErrorToast,
        lastEditedCategoryLabel
    ]);

    useEffect(() => {
        if (isDeletePartnerCategoryLoading) return;
        if (isDeletePartnerCategoryError && deletePartnerCategoryError) {
            showErrorToast(deletePartnerCategoryError, {
                message: `Erreur lors de la suppression de la catégorie "${lastDeletedCategoryLabel}"`
            });
            return;
        }
        if (isDeletePartnerCategorySuccess && deletePartnerCategoryResponse) {
            showApiToast(deletePartnerCategoryResponse, {
                successMessage: `Catégorie "${lastDeletedCategoryLabel}" supprimée avec succès`,
                errorMessage: `Échec de la suppression de la catégorie "${lastDeletedCategoryLabel}"`
            });
            return;
        }
    }, [
        isDeletePartnerCategoryLoading,
        isDeletePartnerCategoryError,
        isDeletePartnerCategorySuccess,
        deletePartnerCategoryError,
        deletePartnerCategoryResponse,
        showErrorToast,
        lastDeletedCategoryLabel
    ]);

    return {
        partners,
        search,
        setSearch,
        partnersFiltered,
        handleNewPartnerCategory,
        handleEditPartnerCategory,
        handleDeletePartnerCategory,
        isPartnerCategoryModalOpen,
        setIsPartnerCategoryModalOpen,
        partnerCategoryModalMode,
        setPartnerCategoryModalMode,
        partnerCategoryToEdit,
        setPartnerCategoryToEdit,
        partnerCategoryToDelete,
        setPartnerCategoryToDelete,
        handleModalTitle,
        handleModalYesText,
        isPartnerModalOpen,
        setIsPartnerModalOpen,
        partnerToDelete,
        setPartnerToDelete,
        handleDeletePartner,
        isPartnersLoading,
        isCreatePartnerCategoryLoading,
        isDeletePartnerCategoryLoading,
        isUpdatePartnerCategoryLoading,
        isDeletePartnerLoading,
        handleSearch,
        filter,
        setFilter,
        setFilterIsOpen,
        filterIsOpen
    };
}
